import { Component, ErrorInfo, ReactNode } from 'react';
import { Result, Button } from 'antd';
import { BugOutlined } from '@ant-design/icons';

type Props = {
  children: ReactNode;
};

type State = {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
};

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            backgroundColor: '#f0f2f5',
            paddingTop: '15vh',
            overflow: 'auto',
          }}
        >
          <Result
            status='error'
            icon={<BugOutlined style={{ color: '#ff4d4f' }} />}
            title='Ups, da ist etwas schiefgelaufen!'
            subTitle={
              <div>
                <p>
                  Es ist ein unerwarteter Fehler aufgetreten. Bitte versuche die Seite neu zu laden.
                </p>
                <p style={{ marginTop: 8 }}>
                  Sollte der Fehler weiterhin auftreten, kontaktiere bitte unseren Support unter{' '}
                  <a href='mailto:support@mendato.com'>support@mendato.com</a>.
                </p>
              </div>
            }
            extra={[
              <Button type='primary' onClick={this.handleReload} key='reload'>
                Seite neu laden
              </Button>,
              <Button key='home' onClick={() => (window.location.href = '/')}>
                Zur Startseite
              </Button>,
            ]}
          >
            {import.meta.env.DEV && this.state.error && (
              <div
                style={{
                  marginTop: 24,
                  padding: 16,
                  backgroundColor: '#fff',
                  border: '1px solid #d9d9d9',
                  borderRadius: 4,
                  textAlign: 'left',
                  maxWidth: 800,
                  margin: '24px auto 0',
                  overflow: 'auto',
                }}
              >
                <h4 style={{ marginBottom: 8, color: '#ff4d4f' }}>
                  Fehlerdetails (nur in Entwicklung sichtbar):
                </h4>
                <pre style={{ margin: 0, fontSize: 12 }}>
                  {this.state.error.toString()}
                  {this.state.errorInfo && this.state.errorInfo.componentStack}
                </pre>
              </div>
            )}
          </Result>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
